import { useState, useCallback } from 'react';
import styles from './SiteUrlInputDialog.module.css';

/**
 * SiteUrlInputDialog - Dialog for entering Google Search Console site URL
 * @param {function} onUrlSubmit - Callback when URL is submitted
 * @param {function} onCancel - Callback when dialog is cancelled
 * @param {boolean} isVerifying - Whether verification is in progress
 * @param {string} error - Error message to display
 */
const SiteUrlInputDialog = ({
    onUrlSubmit,
    onCancel,
    isVerifying = false,
    error = null
}) => {
    const [siteUrl, setSiteUrl] = useState('https://');
    const [isValidUrl, setIsValidUrl] = useState(false);

    const validateUrl = useCallback((url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
        } catch {
            return false;
        }
    }, []);

    const handleUrlChange = useCallback((event) => {
        const newUrl = event.target.value;
        setSiteUrl(newUrl);
        setIsValidUrl(validateUrl(newUrl));
    }, [validateUrl]);

    const handleSubmit = useCallback(() => {
        if (isValidUrl && !isVerifying) {
            onUrlSubmit(siteUrl);
        }
    }, [siteUrl, isValidUrl, isVerifying, onUrlSubmit]);

    const handleKeyPress = useCallback((event) => {
        if (event.key === 'Enter') {
            handleSubmit();
        }
    }, [handleSubmit]);

    return (
        <>
            <div className={styles.overlay} onClick={onCancel} />
            <div className={styles.dialog}>
                <div className={styles.dialogHeader}>
                    <h3 className={styles.dialogTitle}>Enter Your Site URL</h3>
                </div>
                <div className={styles.dialogContent}>
                    <p className={styles.dialogDescription}>
                        Please enter the URL of your website as it appears in Google Search Console.
                    </p>
                    <div className={styles.inputWrapper}>
                        <input
                            type="text"
                            value={siteUrl}
                            onChange={handleUrlChange}
                            onKeyPress={handleKeyPress}
                            placeholder="https://example.com"
                            className={`${styles.urlInput} ${error ? styles.inputError : ''}`}
                            disabled={isVerifying}
                            autoFocus
                        />
                        {error && (
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        )}
                        <div className={styles.helpText}>
                            Make sure this URL matches exactly how your property appears in Google Search Console.
                        </div>
                    </div>
                </div>
                <div className={styles.dialogFooter}>
                    <button 
                        className={styles.cancelButton} 
                        onClick={onCancel}
                        disabled={isVerifying}
                    >
                        Cancel
                    </button>
                    <button 
                        className={styles.confirmButton} 
                        onClick={handleSubmit}
                        disabled={!isValidUrl || isVerifying}
                    >
                        {isVerifying ? 'Verifying...' : 'Verify Access'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default SiteUrlInputDialog;